export interface AiSearchQuery {
  query: string;
  generate_mode?: 'summarize' | 'none';
  display_mode?: 'full' | 'minimal';
  site?: string;
}

export interface AiSearchSource {
  title: string;
  url: string;
  snippet?: string;
  score?: number;
}

export interface AiSearchResult {
  query: string;
  summary?: string;
  sources: AiSearchSource[];
  result_count: number;
  latency_ms?: number;
}

export interface AiSearchError {
  error: string;
  message: string;
  status: number;
}

const RATE_LIMIT = {
  MIN_QUERY_LENGTH: 3,
  DEBOUNCE_MS: 500,
  MAX_REQUESTS_PER_MINUTE: 10,
};

class RateLimiter {
  private requests: number[] = [];
  private lastRequestTime = 0;

  canMakeRequest(): boolean {
    const now = Date.now();

    if (now - this.lastRequestTime < RATE_LIMIT.DEBOUNCE_MS) {
      return false;
    }

    const oneMinuteAgo = now - 60000;
    this.requests = this.requests.filter((time) => time > oneMinuteAgo);

    if (this.requests.length >= RATE_LIMIT.MAX_REQUESTS_PER_MINUTE) {
      return false;
    }

    return true;
  }

  recordRequest(): void {
    const now = Date.now();
    this.requests.push(now);
    this.lastRequestTime = now;
  }

  getRemainingRequests(): number {
    const oneMinuteAgo = Date.now() - 60000;
    this.requests = this.requests.filter((time) => time > oneMinuteAgo);
    return Math.max(0, RATE_LIMIT.MAX_REQUESTS_PER_MINUTE - this.requests.length);
  }

  getTimeUntilNextRequest(): number {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    return Math.max(0, RATE_LIMIT.DEBOUNCE_MS - timeSinceLastRequest);
  }
}

export class AiSearchClient {
  private baseUrl: string;
  private rateLimiter: RateLimiter;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
    this.rateLimiter = new RateLimiter();
  }

  async search(query: string, options: Omit<AiSearchQuery, 'query'> = {}): Promise<AiSearchResult> {
    if (query.trim().length < RATE_LIMIT.MIN_QUERY_LENGTH) {
      throw {
        error: 'ValidationError',
        message: `Query must be at least ${RATE_LIMIT.MIN_QUERY_LENGTH} characters`,
        status: 400,
      } as AiSearchError;
    }

    if (!this.rateLimiter.canMakeRequest()) {
      const timeUntilNext = this.rateLimiter.getTimeUntilNextRequest();
      const remaining = this.rateLimiter.getRemainingRequests();

      if (timeUntilNext > 0) {
        throw {
          error: 'RateLimitError',
          message: `Please wait ${Math.ceil(timeUntilNext / 1000)} seconds before searching again`,
          status: 429,
        } as AiSearchError;
      }

      throw {
        error: 'RateLimitError',
        message: `Rate limit exceeded. ${remaining} requests remaining. Try again in a minute.`,
        status: 429,
      } as AiSearchError;
    }

    const startTime = Date.now();

    const params = new URLSearchParams({
      query: query.trim(),
      generate_mode: options.generate_mode || 'summarize',
      display_mode: options.display_mode || 'full',
      site: options.site || 'all',
    });

    try {
      this.rateLimiter.recordRequest();

      const response = await fetch(`${this.baseUrl}/api/ai-search?${params.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({
          error: 'UnknownError',
          message: 'An unknown error occurred',
        }))) as { error?: string; message?: string };

        throw {
          error: errorData.error || 'SearchError',
          message: errorData.message || response.statusText,
          status: response.status,
        } as AiSearchError;
      }

      const data = (await response.json()) as {
        summary?: string;
        sources?: AiSearchSource[];
      };
      const latency_ms = Date.now() - startTime;

      return {
        query,
        summary: data.summary,
        sources: data.sources || [],
        result_count: data.sources?.length || 0,
        latency_ms,
      };
    } catch (error) {
      if ((error as AiSearchError).status) {
        throw error;
      }

      throw {
        error: 'NetworkError',
        message: error instanceof Error ? error.message : 'Failed to connect to search service',
        status: 0,
      } as AiSearchError;
    }
  }
}

export const aiSearchClient = new AiSearchClient();
